'use client';

import {
  type DehydratedState,
  HydrationBoundary,
  hydrate,
  useQueryClient,
} from '@tanstack/react-query';
import { type ReactNode, useMemo } from 'react';

interface EagerHydrationBoundaryProps {
  state: DehydratedState;
  children: ReactNode;
}

/**
 * `HydrationBoundary` fills a query that already exists in the cache only in an
 * effect, which never runs on the server: a query the header opened empty (the
 * futures pairs) left the server HTML on skeletons. Queries still without data
 * are filled here during render; the boundary handles the rest as usual.
 */
export function EagerHydrationBoundary({ state, children }: EagerHydrationBoundaryProps) {
  const client = useQueryClient();

  useMemo(() => {
    const cache = client.getQueryCache();
    const queries = state.queries.filter(
      (query) => cache.get(query.queryHash)?.state.data === undefined,
    );
    if (queries.length > 0) hydrate(client, { queries });
  }, [client, state]);

  return <HydrationBoundary state={state}>{children}</HydrationBoundary>;
}
